import { SEARCH_PATTERNS, SEARCH_CONFIG } from "../constants/searchConfig";
import { extractLinkedInVanity } from "@/app/components/utils/linkedinUtils";

const NAME_PATTERN = /^[\p{L}\s'-]+$/u;
const HONORIFIC_PREFIX_RE = /^(Dr\.?|Prof\.?|Mr\.?|Mrs\.?|Ms\.?|Mx\.?|Sir|Dame)\s+/i;
const PARTIAL_EMAIL_PATTERN = /^[^\s@]+@[^\s@]*$/;

export const detectSearchType = input => {
  const rawInput = (input || "").trim();
  if (!rawInput) return "discover";

  // Strip leading honorific titles so "Dr. Neda Ghazi" routes as a name search
  const stripped = rawInput.replace(HONORIFIC_PREFIX_RE, "").trim();
  const effective = stripped || rawInput;
  const cleanInput = effective.toLowerCase();

  // Treat only compact local@domain-style input as email. Queries like
  // "Ali Monam @ Meex" should remain discover searches.
  if (PARTIAL_EMAIL_PATTERN.test(cleanInput)) {
    return "email";
  }

  // Enhanced LinkedIn detection - more flexible patterns
  if (isValidLinkedIn(rawInput)) {
    const vanity = extractLinkedInVanity(rawInput);
    return vanity ? "linkedin" : "unknown";
  }

  if (SEARCH_PATTERNS.linkedinDomainLike.test(rawInput)) {
    return "linkedin";
  }

  // Treat only real LinkedIn profile URL shapes as LinkedIn search.
  // Plain text like "CEO of LinkedIn" must remain a discover query.
  const normalized = rawInput.replace(/\s+/g, "");
  if (SEARCH_PATTERNS.linkedinPathLike.test(normalized)) {
    return "linkedin";
  }

  // Detect single-word names (e.g., "Brian") to route to second step for workplace.
  // These are validated names that need company disambiguation.
  const words = effective.split(/\s+/).filter(word => word.length > 0);

  if (
    words.length === 1 &&
    NAME_PATTERN.test(effective) &&
    effective.length >= 2
  ) {
    return "name";
  }

  // Everything else (including multi-word names like "Brian Chesky") goes to discover.
  // The backend will classify it as name/role/company via OpenAI.
  return "discover";
};

export const isValidEmail = email => {
  return SEARCH_PATTERNS.email.test(email);
};

export const isValidLinkedIn = url => {
  // Enhanced validation - check multiple patterns
  return (
    SEARCH_PATTERNS.linkedin.test(url) ||
    SEARCH_PATTERNS.linkedinFlexible.test(url)
  );
};

export const isValidName = name => {
  if (!name || !name.trim()) return false;

  // Check if name contains only valid characters (letters, spaces, apostrophes, hyphens)
  if (!NAME_PATTERN.test(name.trim())) return false;

  const words = name
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0);
  return (
    words.length >= SEARCH_CONFIG.minNameWords &&
    words.every(word => word.length >= SEARCH_CONFIG.minWordLength)
  );
};

export const validateSearchInput = (searchQuery, currentType) => {
  if (!searchQuery.trim()) return false;

  switch (currentType) {
    case "email":
      return isValidEmail(searchQuery);

    case "linkedin":
      // For LinkedIn, check if we can extract a valid vanity
      const vanity = extractLinkedInVanity(searchQuery);
      return vanity && vanity.length >= 3; // Minimum 3 chars for vanity

    case "discover":
      // Valid when query has at least 2 words
      return (
        searchQuery
          .trim()
          .split(/\s+/)
          .filter(w => w.length > 0).length >= 2
      );

    default:
      return false;
  }
};

export const isSearchValid = (searchQuery, detectSearchType) => {
  if (!searchQuery.trim()) return false;

  const type = detectSearchType(searchQuery);
  return validateSearchInput(searchQuery, type);
};
